/**
 * @file index.js
 * @module packages/days/src
 * @description This module serves as the main entry point for the 'days' package.
 * It exports key services and utility functions related to day management
 * within a trip, making them available for use in other parts of the application.
 */

// Export React components using ES6 syntax
export { default as DaysList } from './components/DaysList.jsx';
export { default as DayDetailPage } from './components/DayDetailPage.js';

// Import ES6 modules
import { daysService } from './services/daysService';
import * as dayEditUtils from './utils/dayEditUtils'; // Convert to ES6 import for consistency
import * as accommodationDayUtils from './utils/accommodationDayUtils';
import * as contentBlockUtils from './utils/contentBlockUtils';
import * as contentBlockSchema from './schemas/contentBlock.schema';
import { backupRegistry } from '@trip-planner-monorepo/shared';
import { db } from '@trip-planner-monorepo/shared';
import { doc, setDoc } from 'firebase/firestore';

// Export initialization function
export const initDaysServices = () => {
  // Register backup capabilities with the backup registry
  backupRegistry.register('days', {
    fetchAll: async (userId, tripId) => {
      console.log(`[Days] Fetching all days for backup: userId=${userId}, tripId=${tripId}`);
      return await daysService.fetchDays(userId, tripId);
    },
    restoreAll: async (userId, tripId, data) => {
      console.log(`[Days] Restoring ${data.length} days for trip ${tripId}`);

      // Data is an array of day documents from backup
      for (const dayData of data) {
        try {
          // Preserve original ID by using setDoc instead of addDoc
          const dayRef = doc(db, 'users', userId, 'trips', tripId, 'days', dayData.id);

          // Remove the id from the data object (it's already in the document reference)
          const { id, ...dayDataWithoutId } = dayData;

          // Use setDoc to create/overwrite document with preserved ID
          await setDoc(dayRef, {
            ...dayDataWithoutId,
            updatedAt: new Date().toISOString()
          });

          console.log(`[Days] Restored day ${dayData.id} (${dayData.date || 'no date'})`);
        } catch (error) {
          console.error(`[Days] Error restoring day ${dayData.id}:`, error);
          throw error;
        }
      }

      console.log(`[Days] Successfully restored ${data.length} days`);
    },
    version: '1.0'
  });

  console.log('[Days] Registered with backup registry');

  // Initialize the days service
  daysService.init();
  console.log('Days services initialized');
};

// Export services and utils using ES6 syntax for consistent package interface
export { daysService };
export { dayEditUtils };
export { accommodationDayUtils };
export { contentBlockUtils };
export { contentBlockSchema };

// Export specific functions that are needed by other packages
export const { formatDayContentWithAccommodationEntry, calculateDaysListDateRange } = accommodationDayUtils;

// Export key content block functions for external use
export const {
  validateContentBlock,
  createContentBlock,
  isStructuredContent,
  consolidateContentBlocks
} = contentBlockUtils; 